/**
 * The bill behind an expense, made visible.
 *
 * Expenses carry a Cloudinary URL and nothing else, so the thumbnail is
 * derived from that URL rather than stored: a width-limited transform for
 * images, and the page-1 `.jpg` rendering for PDFs. Bills uploaded before
 * PDFs moved off `raw` storage have no preview to derive, so those fall back
 * to a labelled chip that opens the file.
 */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FileText, X, ExternalLink, Image as ImageIcon } from 'lucide-react';

const isCloudinary = (url) => /res\.cloudinary\.com\/.+\/image\/upload\//.test(url || '');
export const isPdfBill = (url) => /\.pdf(\?|$)/i.test(url || '');
/** Old `raw/upload` PDFs: no derived rendering exists for these. */
export const isLegacyRaw = (url) => /res\.cloudinary\.com\/.+\/raw\/upload\//.test(url || '');

/** A small, fast rendering of the bill — page 1 for a PDF. */
export const billThumb = (url, width = 160) => {
  if (!isCloudinary(url)) return '';
  const transformed = url.replace('/upload/', `/upload/c_limit,w_${width},q_auto,f_jpg/`);
  return isPdfBill(url) ? transformed.replace(/\.pdf(\?|$)/i, '.jpg$1') : transformed;
};

/** Full-size for the viewer, still capped so a 12 MP phone photo opens fast. */
const billFull = (url) => {
  if (!isCloudinary(url)) return url;
  const transformed = url.replace('/upload/', '/upload/c_limit,w_1600,q_auto,f_jpg/');
  return isPdfBill(url) ? transformed.replace(/\.pdf(\?|$)/i, '.jpg$1') : transformed;
};

/** Thumbnail button: the quick reference, straight in the row or the form. */
export const BillThumb = ({ url, onOpen, size = 'sm', testId }) => {
  if (!url) return null;
  const box = size === 'lg' ? 'h-24 w-20' : 'h-9 w-9';

  if (isLegacyRaw(url)) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${box} inline-flex flex-col items-center justify-center gap-0.5 rounded border border-slate-300 bg-slate-50 text-[9px] font-semibold text-slate-500 hover:border-teal-400 hover:text-teal-700`}
        title="Open the bill (older upload — download to view)"
        data-testid={testId || 'bill-thumb'}
      >
        <FileText size={size === 'lg' ? 20 : 14} />
        FILE
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(url)}
      className={`${box} group relative overflow-hidden rounded border border-slate-300 bg-slate-100 hover:border-teal-500`}
      title="View the bill"
      data-testid={testId || 'bill-thumb'}
    >
      <img
        src={billThumb(url, size === 'lg' ? 320 : 120)}
        alt="Bill"
        className="h-full w-full object-cover"
        loading="lazy"
      />
      {isPdfBill(url) && (
        <span className="absolute bottom-0 right-0 bg-slate-900/80 px-1 text-[8px] font-bold text-white">
          PDF
        </span>
      )}
    </button>
  );
};

/** The bill itself, over the page — Esc or a click outside closes it. */
export const BillViewer = ({ url, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!url) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/80 p-4"
      onClick={onClose}
      data-testid="bill-viewer"
    >
      <div
        className="relative max-h-full w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-800">
            {isPdfBill(url) ? <FileText size={15} /> : <ImageIcon size={15} />} Bill / receipt
          </span>
          <div className="flex items-center gap-3">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-900"
              data-testid="bill-viewer-original"
            >
              Open original <ExternalLink size={12} />
            </a>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700"
              data-testid="bill-viewer-close"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="max-h-[75vh] overflow-auto bg-slate-50 p-3 text-center">
          <img
            src={billFull(url)}
            alt="Bill"
            className="mx-auto max-w-full rounded"
            data-testid="bill-viewer-image"
          />
          {isPdfBill(url) && (
            <p className="mt-2 text-[11px] text-slate-500">
              First page shown — use <b>Open original</b> for the full PDF.
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BillViewer;
